/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ConfigProvider, notification } from 'antd';
import { StyleSheetManager } from 'styled-components';

import PrivateRoute from '../containers/PrivateRoute';
import Login from '../containers/Login';
import Main from '../containers/Main';
import { LOCATION } from '../constants/location';
import Blank from '../containers/Blank';
import Error from './Error';
import Unknown from './Unknown';
import { checkAsyncActionPermissionsWithLaunch } from '../constants/authorization';
import { CANCEL_REQUEST_MESSAGE } from '../constants/common';
import useCopyChartTooltipData from '../utils/hooks/useCopyChartTooltipData';

const Root = (props) => {
  const {
    tenantId,
    applicationError,
    resetErrorMessage,
    store,
    tenants,
    setTenantId,
    logout,
    isAuthenticated,
    fetchConfig,
    updateLocation,
    updatePreviousLocation,
    location,
  } = props;

  useCopyChartTooltipData();

  const { pathname } = useLocation();

  useLayoutEffect(() => {
    updatePreviousLocation(location);
    updateLocation(pathname);
  }, [pathname]);

  const fetchData = async () => {
    const {
      resetApp,
      fetchSegments,
      fetchOptions,
      fetchMetainf,
      fetchMapping,
      fetchPermissions,
      fetchLabels,
      fetchFunnels,
      fetchDashboards,
      fetchTemplates,
      setApplicationReady,
      checkActualization,
    } = props;

    resetApp();

    if (tenantId && isAuthenticated) {
      // We have to fetch permissions before other loads
      const { permissions } = await fetchPermissions();
      const permissionsSet = new Set(permissions);
      // We have to fetch metainf before segments
      await checkAsyncActionPermissionsWithLaunch(fetchMetainf, permissionsSet);

      await Promise.all(
        [
          fetchSegments,
          fetchOptions,
          fetchMapping,
          fetchLabels,
          fetchFunnels,
          fetchTemplates,
          fetchDashboards,
        ].map((item) => checkAsyncActionPermissionsWithLaunch(item, permissionsSet))
      );
      checkActualization();
    }
    setApplicationReady();
  };

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId]);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (applicationError === CANCEL_REQUEST_MESSAGE) {
      notification.warning({
        description: applicationError,
        key: applicationError,
        duration: 5,
        message: 'Warning',
        onClose: resetErrorMessage,
        placement: 'bottomRight',
      });
    } else if (applicationError) {
      notification.error({
        description: applicationError,
        key: applicationError,
        duration: 5,
        message: 'Error',
        onClose: resetErrorMessage,
        placement: 'bottomRight',
      });
    }
  });

  // срабатывает если массив тенантов меняется.
  // после action: login, когда тенанты были получены, отработает данный код.
  useMemo(() => {
    // если тенантов нет, но при этом выполнен логин, то необходимо сделать logout.
    if (!tenants.length && isAuthenticated) {
      logout();
    }
    // если получен только 1 тенант, то он устанавливается дефолтным.
    else if (tenants.length === 1) {
      setTenantId(tenants[0]);
    }
  }, [tenants]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorText: 'rgba(0,0,0,.65)',
          fontSize: 16,
          lineHeight: 1.5715,
        },
      }}
    >
      <Provider store={store}>
        <StyleSheetManager disableVendorPrefixes={process.env.NODE_ENV === 'development'}>
          <Routes>
            <Route path={LOCATION.LOGIN} element={<Login />} />
            <Route path={`/:tenantId${LOCATION.LOGIN}`} element={<Login />} />
            <Route
              path={LOCATION.ERROR}
              element={
                <PrivateRoute>
                  <Error />
                </PrivateRoute>
              }
            />
            <Route
              path="/:tenantId/*"
              element={
                <PrivateRoute>
                  <Main />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Blank />
                </PrivateRoute>
              }
            />
            <Route
              path={LOCATION.UNKNOWN}
              element={
                <PrivateRoute>
                  <Unknown />
                </PrivateRoute>
              }
            />
          </Routes>
        </StyleSheetManager>
      </Provider>
    </ConfigProvider>
  );
};

export default Root;
